import { Component, OnInit, inject, signal, input, computed, LOCALE_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StockService } from '../../../../services/product/stock/stock';
import { SuppliersService } from '../../../../services/suppliers/suppliers.service';
import {
  CurrentStockItem,
  StockFilters,
  StockType,
  ProductTitleResult,
} from '../../../../pages/admin-dashboard/stock-management/stock.model';
import { AdminSuppliersList } from '../../../../pages/admin-dashboard/admin-supplier/admin-supplier.model';
import { Loading } from '../../../../utils/loading/loading';
import { StockFiltersComponent } from '../../admin-stock-list/stock-filters-component/stock-filters-component';
import { CurrentStockTableComponent } from '../../admin-stock-list/current-stock-table-component/current-stock-table-component';
import { ErrorData } from '../../../../utils/error-data/error-data';

@Component({
  selector: 'app-product-current-stock',
  standalone: true,
  imports: [CommonModule, StockFiltersComponent, CurrentStockTableComponent, Loading, ErrorData],
  templateUrl: './product-current-stock.html',
})
export class ProductCurrentStock implements OnInit {
  private stockService = inject(StockService);
  private suppliersService = inject(SuppliersService);
  private locale = inject(LOCALE_ID);

  productId = input.required<number>();
  productType = input.required<string>();

  loading = signal(false);
  error = signal<boolean>(false);
  allStockData = signal<CurrentStockItem[]>([]);
  suppliers = signal<AdminSuppliersList[]>([]);
  stockTypes = signal<StockType[]>([]);
  totalElements = signal(0);
  currentPage = signal(0);
  itemsPerPage = 10;

  productSearchTerm = signal('');
  showProductTitleDropdown = signal(false);
  productTitleResults = signal<ProductTitleResult[]>([]);

  readonly noDataTitle = $localize`:@@stock.noRecords:No Stock Records Found`;
  readonly noDataSubTitle = $localize`:@@stock.tryAdjustingFilters:Try adjusting your filters or add a new stock entry.`;

  filters = signal<StockFilters>({
    productId: undefined,
    supplierId: undefined,
    expDateFrom: null,
    expDateTo: null,
    isActive: undefined,
    stockType: undefined,
    pageNum: 0,
    pageSize: 10,
    orderBy: 'LAST_MODIFIED_ON',
    orderDir: 'DESC',
  });

  isArabic = computed(() => this.locale.startsWith('ar'));
  hasSubProducts = computed(() => {
    const idStr = this.productId().toString();
    const type = this.productType();

    return type === 'HAS_SUB_PRODUCTS' || idStr.startsWith('2');
  });
  ngOnInit(): void {
    this.filters.update((f) => ({ ...f, productId: this.productId() }));
    this.loadSuppliers();
    this.loadStockTypes();
    this.loadData();
  }

  loadData(): void {
    this.loading.set(true);
    this.error.set(false);

    const f = this.filters();
    const params: StockFilters = {
      productId: f.productId || this.productId(),
      supplierId: f.supplierId || undefined,
      expDateFrom: f.expDateFrom || null,
      expDateTo: f.expDateTo || null,
      isActive: f.isActive,
      pageNum: this.currentPage(),
      pageSize: this.itemsPerPage,
      orderBy: f.orderBy,
      orderDir: f.orderDir,
    };

    this.stockService.getCurrentStock(params).subscribe({
      next: (res) => {
        this.allStockData.set(res.list || []);
        this.totalElements.set(res.total || 0);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      },
    });
  }

  loadSuppliers(): void {
    this.suppliersService.getAllSuppliers({ pageSize: 100, isActive: true }).subscribe({
      next: (res) => this.suppliers.set(res.list || []),
    });
  }

  loadStockTypes(): void {
    this.stockService.getStockTypes().subscribe({
      next: (res) => this.stockTypes.set(res.list || []),
    });
  }

  toggleOrder(): void {
    this.filters.update((f) => ({ ...f, orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC' }));
    this.loadData();
  }

  resetFilters(): void {
    this.filters.set({
      productId: this.productId(),
      supplierId: undefined,
      expDateFrom: null,
      expDateTo: null,
      isActive: undefined,
      stockType: undefined,
      pageNum: 0,
      pageSize: this.itemsPerPage,
      orderBy: 'LAST_MODIFIED_ON',
      orderDir: 'DESC',
    });
    this.productSearchTerm.set('');
    this.currentPage.set(0);
    this.loadData();
  }

  totalPages = computed(() => Math.ceil(this.totalElements() / this.itemsPerPage));
  startItem = computed(() => (this.totalElements() === 0 ? 0 : this.currentPage() * this.itemsPerPage + 1));

  previousPage(): void {
    if (this.currentPage() > 0) {
      this.currentPage.update((p) => p - 1);
      this.loadData();
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages() - 1) {
      this.currentPage.update((p) => p + 1);
      this.loadData();
    }
  }

  goToPage(page: number): void {
    this.currentPage.set(page - 1);
    this.loadData();
  }

  getPageNumbers(): number[] {
    const total = this.totalPages();
    const current = this.currentPage() + 1;
    let pages: number[] = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      if (current <= 3) pages = [1, 2, 3, -1, total];
      else if (current >= total - 2) pages = [1, -1, total - 2, total - 1, total];
      else pages = [1, -1, current, -1, total];
    }
    return pages;
  }
}
